import { ExternalLink, Minus, Plus, ShieldAlert, ShieldCheck, ShieldOff } from "lucide-react";
import { useMemo } from "react";
import { Link } from "react-router";

import {
  AdminConsentBadge,
  PrivilegedBadge,
  RiskBadge,
} from "@/components/app-registration-badges";
import { Badge } from "@/components/ui/badge";
import { useAppRegistrationScopes } from "@/lib/api";
import { diffScopeKeys, indexScopes, type ScopeIndex } from "@/lib/app-registration-scopes";
import { cn } from "@/lib/utils";

/** Where a Scaffold UI admin gets a sign-in registration's consent from IT. */
export const IT_APPROVALS_PATH = "/admin/it-approvals";

/**
 * What a permission change does, and what it pointedly does not do.
 *
 * Shared by both sides of the desk for the usual reason — the requester
 * composing an amendment and the approver deciding it have to be looking at
 * the same thing — but here it is more than consistency. The consent
 * consequence below is the whole reason amending is not a one-line feature,
 * and it is the sentence most likely to be softened into something
 * reassuring if each screen writes its own.
 */

interface PermissionDiffProps {
  /** What the registration holds today. */
  base: readonly string[];
  /** What it would hold after the change. */
  requested: readonly string[];
  className?: string;
}

/**
 * The change itself: what is being added, what is being removed, what stays.
 *
 * Removals are shown as loudly as additions. A narrowing looks like the safe
 * direction and is the one this desk cannot actually complete — see
 * `ConsentConsequences` — so hiding it behind "3 permissions" would bury the
 * half that needs a human.
 */
export function PermissionDiff({ base, requested, className }: PermissionDiffProps) {
  const { data: scopes } = useAppRegistrationScopes();
  const catalogue = useMemo(() => indexScopes(scopes), [scopes]);
  const diff = useMemo(() => diffScopeKeys(base, requested), [base, requested]);

  if (diff.added.length === 0 && diff.removed.length === 0) {
    return (
      <p className={cn("text-muted-foreground text-xs", className)}>
        No change to the permissions.
      </p>
    );
  }

  return (
    <div className={cn("space-y-3", className)}>
      {diff.added.length > 0 && (
        <ScopeChangeGroup
          label="Adding"
          keys={diff.added}
          catalogue={catalogue}
          icon={<Plus className="size-3.5 shrink-0" aria-hidden />}
          className="text-green-800 dark:text-green-300"
        />
      )}
      {diff.removed.length > 0 && (
        <ScopeChangeGroup
          label="Removing"
          keys={diff.removed}
          catalogue={catalogue}
          icon={<Minus className="size-3.5 shrink-0" aria-hidden />}
          className="text-red-800 dark:text-red-300"
        />
      )}
      {diff.unchanged.length > 0 && (
        <p className="text-muted-foreground text-xs">
          Keeping {diff.unchanged.length} permission{diff.unchanged.length === 1 ? "" : "s"}{" "}
          unchanged.
        </p>
      )}
    </div>
  );
}

function ScopeChangeGroup({
  label,
  keys,
  catalogue,
  icon,
  className,
}: {
  label: string;
  keys: string[];
  catalogue: ScopeIndex;
  icon: React.ReactNode;
  className?: string;
}) {
  return (
    <div className="space-y-1.5">
      <p className={cn("flex items-center gap-1.5 text-xs font-medium", className)}>
        {icon}
        {label}
      </p>
      <ul className="flex flex-wrap gap-1.5">
        {keys.map((key) => {
          const scope = catalogue.get(key);
          // The raw key rather than nothing when the catalogue has not
          // arrived or no longer lists it: a permission silently missing from
          // a diff is the one failure mode this must not have.
          if (!scope) {
            return (
              <li key={key}>
                <Badge variant="outline" className="font-mono">
                  {key}
                </Badge>
              </li>
            );
          }
          return (
            <li
              key={key}
              className="inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5"
              title={scope.description}
            >
              <span className="font-mono text-xs">{scope.value}</span>
              {scope.privileged && <PrivilegedBadge />}
              <RiskBadge risk={scope.risk} />
              {scope.admin_consent_required && <AdminConsentBadge />}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

interface ConsentConsequencesProps {
  added: readonly string[];
  removed: readonly string[];
  /** The admin-consent deep link, when the registration has one recorded. */
  consentUrl?: string | null;
  /**
   * The project's own sign-in registration. Its consent is not a link handed
   * to the requester: an approved addition puts it back in the IT approval
   * queue, and what IT already granted keeps working meanwhile.
   */
  signInRegistration?: boolean;
  /** Link the sign-in variant to the IT approvals page — for admin-only screens. */
  linkToItApprovals?: boolean;
  className?: string;
}

/**
 * What a human still has to do, stated per direction.
 *
 * Scaffold UI holds Graph's `Application.ReadWrite.OwnedBy` and deliberately
 * **not** `DelegatedPermissionGrant.ReadWrite.All`, so it can change what an
 * application *asks for* and can never touch what the tenant has *granted*.
 * Those are two different objects in Entra, and the gap between them runs in
 * both directions:
 *
 * - **Adding** writes the permission onto the application without extending
 *   the existing grant, so the new scope silently fails until a directory
 *   admin consents again.
 * - **Removing** leaves the old grant live. Tokens can still carry the
 *   permission until a human revokes it in the portal. An approver who reads a
 *   narrowing as "revoked" has been told something false about a security
 *   control, which is why this renders as a warning rather than a footnote.
 *
 * Renders nothing when nothing moved — there is no consequence to state.
 */
export function ConsentConsequences({
  added,
  removed,
  consentUrl,
  signInRegistration = false,
  linkToItApprovals = false,
  className,
}: ConsentConsequencesProps) {
  if (added.length === 0 && removed.length === 0) {
    return null;
  }

  return (
    <div className={cn("space-y-2", className)}>
      {added.length > 0 && (
        <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 dark:border-amber-800 dark:bg-amber-950/40">
          <ShieldAlert className="mt-0.5 size-4 shrink-0 text-amber-600 dark:text-amber-400" />
          {signInRegistration ? (
            <div className="space-y-1 text-xs text-amber-900 dark:text-amber-200">
              <p className="font-medium">Added permissions go back to IT for approval</p>
              <p>
                Entra does not extend the existing consent when an application&apos;s permission
                list grows, so once this change is approved the registration goes back to IT for
                approval. Whatever IT has already approved, sign-in included, keeps working
                meanwhile; the added {added.length === 1 ? "permission works" : "permissions work"}{" "}
                once an IT admin approves.
              </p>
              {linkToItApprovals && (
                <Link
                  to={IT_APPROVALS_PATH}
                  className="inline-flex items-center gap-1 font-medium underline"
                >
                  <ShieldCheck className="size-3" /> Open IT approvals
                </Link>
              )}
            </div>
          ) : (
            <div className="space-y-1 text-xs text-amber-900 dark:text-amber-200">
              <p className="font-medium">Added permissions will not work yet</p>
              <p>
                Entra does not extend the existing consent when an application&apos;s permission
                list grows, so a directory admin has to grant admin consent again before{" "}
                {added.length === 1 ? "it works" : "they work"}.
              </p>
              {consentUrl && (
                <a
                  href={consentUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 font-medium underline"
                >
                  Open the consent link <ExternalLink className="size-3" />
                </a>
              )}
            </div>
          )}
        </div>
      )}
      {removed.length > 0 && (
        <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 dark:border-red-800 dark:bg-red-950/40">
          <ShieldOff className="mt-0.5 size-4 shrink-0 text-red-600 dark:text-red-400" />
          <div className="space-y-1 text-xs text-red-900 dark:text-red-200">
            <p className="font-medium">Removed permissions stay consented</p>
            <p>
              Taking a permission off the application does not revoke the tenant&apos;s existing
              grant, so tokens can still carry {removed.length === 1 ? "it" : "them"} until a human
              removes the grant by hand under{" "}
              <span className="font-medium">
                Entra ID &rsaquo; Enterprise applications &rsaquo; this app &rsaquo; Permissions
              </span>
              . This desk cannot do it: it is not allowed to touch consent grants, by design.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
